import { getAccessToken } from '../authentication/supabaseService';
import { getDemoAccountUserIds } from './superAdminSettingsService';
import { WeekDay } from '../restaurant/restaurantService';
import type { AdminReportType, AdminReportLanguage } from './adminService';

const API_BASE = '/api/admin';

/**
 * Report types the Settings tab exposes, in tab order. Widening this array is the whole frontend
 * change needed to expose a new schedulable type — the UI is generic.
 */
export const SETTINGS_REPORT_TYPES: AdminReportType[] = [
  'weekly',
  'revenue',
  'monthly',
  'waste',
  'inventoryOrder',
  'inventoryManagement',
  'inventoryLeakage',
];

/**
 * Types configured PER INVENTORY GROUP rather than per restaurant (server: the registry's
 * `requiresGroup` flag). They are sent when a count session is finalized, not on a send day, so
 * their tabs carry a per-group master switch and per-group recipient toggles instead of the
 * day/time/language controls.
 */
export const GROUP_SCOPED_REPORT_TYPES = ['inventoryOrder', 'inventoryManagement'] as const;

export const isGroupScopedReportType = (reportType: AdminReportType): boolean =>
  (GROUP_SCOPED_REPORT_TYPES as readonly string[]).includes(reportType);

/**
 * Types the scheduled dispatcher may send on a restaurant's send day (server: the registry's
 * `schedulable` flag — `reportTypeRegistry.ts` is the source of truth, keep the two in step).
 * Inventory leakage is handled separately: count date + four days at 14:00, so it has
 * restaurant-level opt-in and language but no configurable day or time.
 */
export const SCHEDULABLE_REPORT_TYPES = ['weekly', 'revenue', 'monthly', 'waste'] as const;

export const isSchedulableReportType = (reportType: AdminReportType): boolean =>
  (SCHEDULABLE_REPORT_TYPES as readonly string[]).includes(reportType);

/**
 * The two send slots (Europe/Amsterdam) a scheduled report can go out in — the cron has one entry
 * per value. Mirrors the `send_time` CHECK on `restaurant_report_schedules`.
 */
export const REPORT_SEND_TIMES = ['11:00', '14:00'] as const;
export type ReportSendTime = (typeof REPORT_SEND_TIMES)[number];

/** Report types the group (admin-account) send supports — the registry's group-capable set. */
export const GROUP_REPORT_TYPES = ['weekly', 'revenue', 'monthly', 'priceAlerts'] as const;
export type GroupReportType = Extract<AdminReportType, (typeof GROUP_REPORT_TYPES)[number]>;

/**
 * Per-type default send day, mirroring `DEFAULT_SEND_DAY_BY_TYPE` in
 * reportSubscriptionsDbService.ts. A type absent here falls back to monday, exactly as the
 * server's map does.
 */
const DEFAULT_SEND_DAY_BY_TYPE: Partial<Record<AdminReportType, WeekDay>> = {
  monthly: 'wednesday',
  waste: 'wednesday',
};

/**
 * Per-type default of the restaurant MASTER SWITCH, mirroring `DEFAULT_ENABLED_BY_TYPE` in
 * reportSubscriptionsDbService.ts. A type absent here defaults to sending, exactly as the
 * server's map does; `inventoryLeakage` stays off until explicitly enabled.
 */
const DEFAULT_ENABLED_BY_TYPE: Partial<Record<AdminReportType, boolean>> = {
  inventoryLeakage: false,
  // `priceAlerts` is absent on purpose (= true), mirroring the server: a send of that report
  // covers the owner's restaurants, and no per-restaurant switch gates it. It is unrelated to
  // `users.receive_price_alert_emails`, which opts a user out of the email sent every time a
  // price alert is saved — see the server's DEFAULT_ENABLED_BY_TYPE for the full distinction.
};

/** What a restaurant with no schedule row resolves to for this type. */
export const defaultReportEnabled = (reportType: AdminReportType): boolean =>
  DEFAULT_ENABLED_BY_TYPE[reportType] ?? true;

/**
 * What a type with no schedule row anywhere resolves to. Mirrors the server's
 * `scheduleDefaultsFor(reportType)` (reportSubscriptionsDbService.ts) — keep the two in step, or
 * the UI would promise a send day the dispatcher does not use. Only reached when the overview
 * omits the type; whenever any of the owner's restaurants has a row, the server sends the
 * resolved value.
 */
export function ownerScheduleDefaults(reportType: AdminReportType): ReportOwnerSchedule {
  return {
    sendDay: DEFAULT_SEND_DAY_BY_TYPE[reportType] ?? 'monday',
    sendTime: '14:00',
    language: 'nl',
    mixed: false,
  };
}

export interface ReportSettingsStaff {
  id: string;
  name: string | null;
  email: string;
  /** users.restaurant_role. Null when the admin never assigned one (Roles tab). */
  restaurantRole: string | null;
  /** Subscribed to this report type FOR THIS RESTAURANT. No subscription row on the server = false. */
  enabled: boolean;
}

/**
 * Day, slot and language for ONE report type of ONE owner account. Stored per restaurant, but
 * only ever written owner-wide ({@link setReportScheduleForOwner} fans out), so a single control
 * speaks for every venue.
 */
export interface ReportOwnerSchedule {
  sendDay: WeekDay;
  sendTime: ReportSendTime;
  language: AdminReportLanguage;
  /**
   * The owner's restaurants do not all hold the same values — typically a venue created after
   * the last save. The resolved value above is the most recently updated row; the next save
   * unifies them.
   */
  mixed: boolean;
}

/**
 * Does this inventory group produce the count-triggered report? One setting per (restaurant,
 * group, type) — never per user. Recipients stay restaurant-level, so whoever is subscribed to
 * the type receives it for every group switched on here.
 */
export interface ReportSettingsInventoryGroup {
  inventoryGroupId: string;
  groupName: string;
  /** Unlike a restaurant's master switch, a group with no stored entry reads as OFF. */
  enabled: boolean;
}

/**
 * Which unit the LEAKAGE report ('inventoryLeakage') prints its quantities in: 'std' (kg / L / pcs,
 * the long-standing behaviour) or 'count' (each product's starred count unit, "12.00 fles").
 * Per restaurant and super-admin owned — NOT the Leakage page's per-user toggle, since this
 * report goes to recipients who never opened that page. Euro figures are unaffected.
 */
export const LEAKAGE_REPORT_UNIT_MODES = ['std', 'count'] as const;
export type LeakageReportUnitMode = (typeof LEAKAGE_REPORT_UNIT_MODES)[number];

export interface ReportSettingsRestaurant {
  restaurantId: string;
  restaurantName: string;
  /**
   * Master switch PER REPORT TYPE: does this restaurant send that type at all. The server always
   * resolves the requested type, so a missing key only happens for a type this payload was not
   * built for — it then falls back to {@link defaultReportEnabled}, which is per type.
   */
  enabled: Partial<Record<AdminReportType, boolean>>;
  /** The owner's own subscription for THIS restaurant — rendered as the first recipient row. */
  ownerEnabled: boolean;
  staff: ReportSettingsStaff[];
  /** Only sent for a group-scoped report type; absent on the scheduled ones. */
  inventoryGroups?: ReportSettingsInventoryGroup[];
  /** Only sent for the 'inventoryLeakage' type, the only one it affects. Absent = 'std'. */
  leakageUnitMode?: LeakageReportUnitMode;
}

export interface ReportSettingsOwner {
  ownerUserId: string;
  ownerName: string | null;
  ownerEmail: string;
  /** Owner-resolved schedule per report type. Absent = the server had no row to resolve from. */
  schedules: Partial<Record<AdminReportType, ReportOwnerSchedule>>;
  restaurants: ReportSettingsRestaurant[];
}

async function authHeaders() {
  const token = await getAccessToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Every owner account grouped with its restaurants and staff: the owner-resolved schedule for
 * `reportType`, each restaurant's master switch, and each user's subscription.
 *
 * Demo accounts are excluded (as admins and as staff) — they mirror real restaurants and never
 * receive reports, so their only effect here is burying the real clients.
 */
export async function getReportSettingsOverview(reportType: AdminReportType): Promise<ReportSettingsOwner[]> {
  const params = new URLSearchParams({ reportType });
  const demoUserIds = await getDemoAccountUserIds();
  if (demoUserIds.length > 0) params.set('excludeUserIds', JSON.stringify(demoUserIds));

  const res = await fetch(`${API_BASE}/reports/settings-overview?${params.toString()}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to fetch report settings overview: ${res.statusText}`);
  const data = await res.json();
  return data.owners ?? [];
}

/**
 * Subscribe/unsubscribe one user (admin or staff) to one report type AT ONE RESTAURANT.
 * Subscriptions are per (user, restaurant, type) — the same user at another restaurant is untouched.
 * Count-triggered types included: which inventory groups report is a separate, per-restaurant
 * setting ({@link setInventoryGroupReportEnabled}), not something a recipient row carries.
 */
export async function setReportSubscription(
  userId: string,
  restaurantId: string,
  reportType: AdminReportType,
  enabled: boolean
): Promise<void> {
  const res = await fetch(`${API_BASE}/reports/subscription`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify({ userId, restaurantId, reportType, enabled }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update subscription: ${res.statusText}`);
  }
}

/**
 * The per-inventory-group master switch for a count-triggered report type. Off means that group's
 * finalized count mails nothing for this type, whatever the recipients below it say — and their
 * toggles stay saved, so turning it back on restores exactly who was selected.
 */
export async function setInventoryGroupReportEnabled(
  restaurantId: string,
  inventoryGroupId: string,
  reportType: AdminReportType,
  enabled: boolean
): Promise<void> {
  const res = await fetch(`${API_BASE}/reports/inventory-group-enabled`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify({ restaurantId, inventoryGroupId, reportType, enabled }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update inventory group switch: ${res.statusText}`);
  }
}

/**
 * The unit the leakage report prints its QUANTITIES in, for one restaurant. Only meaningful
 * for the 'inventoryLeakage' type; every euro figure on the report is unaffected either way.
 */
export async function setLeakageReportUnitMode(
  restaurantId: string,
  mode: LeakageReportUnitMode
): Promise<void> {
  const res = await fetch(`${API_BASE}/reports/leakage-unit-mode`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify({ restaurantId, mode }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update the leakage unit mode: ${res.statusText}`);
  }
}

/**
 * Partial update of ONE OWNER ACCOUNT's schedule for one report type — pass only what changed.
 * The server fans the values out to every restaurant that account owns (storage stays per
 * restaurant), so schedules cannot drift; returns how many restaurants were written.
 * Never touches a restaurant's master switch — that is {@link setRestaurantReportEnabled}.
 */
export async function setReportScheduleForOwner(
  ownerUserId: string,
  reportType: AdminReportType,
  changes: {
    sendDay?: WeekDay;
    sendTime?: ReportSendTime;
    language?: AdminReportLanguage;
  }
): Promise<number> {
  const res = await fetch(`${API_BASE}/reports/schedule`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify({ ownerUserId, reportType, ...changes }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update schedule: ${res.statusText}`);
  }
  const data = await res.json();
  return data.updatedRestaurants ?? 0;
}

/**
 * The per-restaurant master switch. Stays individual on purpose: a restaurant switched off is
 * excluded from everything — its own PDF and the group overview's numbers alike.
 */
export async function setRestaurantReportEnabled(
  restaurantId: string,
  reportType: AdminReportType,
  enabled: boolean
): Promise<void> {
  const res = await fetch(`${API_BASE}/reports/restaurant-enabled`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify({ restaurantId, reportType, enabled }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update the restaurant switch: ${res.statusText}`);
  }
}

export interface GroupReportOwner {
  ownerUserId: string;
  ownerName: string | null;
  restaurants: { id: string; name: string }[];
}

/**
 * Owner accounts a group report can be sent for — only those with 2+ restaurants, since a
 * single-venue "group" is just the individual report.
 */
export async function getGroupReportOwners(): Promise<GroupReportOwner[]> {
  const res = await fetch(`${API_BASE}/reports/group-owners`, { headers: await authHeaders() });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch group owners: ${res.statusText}`);
  }
  const data = await res.json();
  return data.owners ?? [];
}

export interface SendGroupReportResult {
  sent: string[];
  failed: { email: string; error: string }[];
  skipped?: { email: string; reason: 'restrict_main_pages' }[];
  /** Every PDF attached to the email: the group overview plus one per included venue. */
  filenames: string[];
  restaurantsIncluded: { restaurantId: string; name: string }[];
}

/**
 * One combined email per recipient: the group overview PDF plus the individual PDF of every
 * restaurant of that owner with the type enabled. The server resolves which venues are enabled
 * and answers 400 when fewer than two are.
 *
 * `restaurantIds` narrows that set to the venues ticked in the picker — the way to simulate the
 * combined file without every venue. It can only remove: a ticked venue whose type is switched
 * off on Settings stays out, and fewer than two survivors is a 400.
 */
export async function sendGroupReport(params: {
  ownerUserId: string;
  reportType: GroupReportType;
  startDate: string;
  endDate: string;
  language: AdminReportLanguage;
  recipients: string[];
  isTest: boolean;
  restaurantIds?: string[];
}): Promise<SendGroupReportResult> {
  const res = await fetch(`${API_BASE}/reports/send-group-report`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to send the group report: ${res.statusText}`);
  }
  return res.json();
}

/** One staff account the bulk-by-role helper would touch. */
export interface BulkByRoleAffectedUser {
  id: string;
  name: string | null;
  email: string;
  restaurantRole: string | null;
  /** How many of the SCOPED restaurants this user reaches — the rows the apply would write. */
  restaurantCount: number;
}

async function postBulkByRole(body: Record<string, unknown>) {
  const res = await fetch(`${API_BASE}/reports/subscriptions/bulk-by-role`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to apply role defaults: ${res.statusText}`);
  }
  return res.json();
}

/**
 * Dry run: who WOULD be changed, without writing. Always call this before
 * {@link applySubscriptionsByRole} — the helper writes to live customer accounts.
 * Staff only by design: owners/admins have no restaurant_role and are never targeted.
 *
 * `restaurantIds` is the scope and is always explicit — "all" is the full list the Settings
 * tab renders (already demo-filtered), never a wildcard the server would expand itself.
 * The server rejects an empty scope.
 */
export async function previewSubscriptionsByRole(
  reportType: AdminReportType,
  restaurantRoles: string[],
  restaurantIds: string[],
  enabled: boolean
): Promise<BulkByRoleAffectedUser[]> {
  const data = await postBulkByRole({ reportType, restaurantRoles, restaurantIds, enabled, dryRun: true });
  return data.affected ?? [];
}

/** Write the bulk change, within the same scope. Returns how many subscriptions were updated. */
export async function applySubscriptionsByRole(
  reportType: AdminReportType,
  restaurantRoles: string[],
  restaurantIds: string[],
  enabled: boolean
): Promise<number> {
  const data = await postBulkByRole({ reportType, restaurantRoles, restaurantIds, enabled, dryRun: false });
  return data.updatedCount ?? 0;
}
